import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ReactComponent as LinearIcon } from '@elementary/common-ui/assets/16x16/linear.svg';
import {
  Alert,
  Dialog,
  DropDown,
  FlexColumn,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  Loading,
  TextField,
} from '@elementary/common-ui/components';
import { TICKETING } from '@elementary/common-ui/constants/queryKeys';
import { fetchTicketingProjects } from 'shared/services/settingsApi';
import type { CreateTicketDialogProps } from '../CreateTicketDialog';
import TicketCreationError from '../TicketCreationError';

type LinearFormValues = {
  title: string;
  description: string;
};

const CreateLinearTicketDialog = ({
  open,
  onClose,
  onConfirm,
  integration,
  envId,
  isCreating,
  error,
  defaultFields,
  isLoadingDefaults = false,
  contextId,
}: CreateTicketDialogProps) => {
  const hasDefaultFields = isLoadingDefaults || defaultFields !== undefined;

  const { data: teamsData } = useQuery({
    queryKey: [TICKETING, envId, 'linear', 'projects'],
    queryFn: () => fetchTicketingProjects(envId, 'linear'),
    enabled: open && !!envId,
  });

  const teamName = useMemo(() => {
    const team = teamsData?.projects?.find(
      (p) => p.id === integration.project_id,
    );
    return team?.name ?? integration.name;
  }, [teamsData, integration.project_id, integration.name]);

  const { register, watch, reset, formState } = useForm<LinearFormValues>({
    mode: 'onBlur',
  });

  useEffect(() => {
    if (defaultFields) {
      reset({
        title: defaultFields.title || '',
        description: defaultFields.description || '',
      });
    }
  }, [defaultFields, reset]);

  const formValues = watch();

  const isFormValid = useMemo(
    () => !!formValues.title?.trim(),
    [formValues.title],
  );

  const isLoading = hasDefaultFields
    ? isLoadingDefaults || !defaultFields
    : false;

  const onCreateTicket = () => {
    const customFields: Record<string, string> = {};

    if (formValues.title !== defaultFields?.title) {
      customFields.title = formValues.title;
    }
    if (formValues.description !== defaultFields?.description) {
      customFields.description = formValues.description;
    }

    onConfirm(Object.keys(customFields).length > 0 ? customFields : undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      titleIcon={<LinearIcon />}
      title="Create Linear ticket"
      confirmText="Create ticket"
      onConfirmButtonClick={onCreateTicket}
      isLoading={isCreating || isLoading}
      confirmDisabled={!isFormValid}
      customWidth={688}
      message={
        <form
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
          style={{
            height: '560px',
          }}
        >
          {isLoading && <Loading />}

          {!isLoading && (
            <FlexColumn gap={2}>
              {error &&
                (contextId ? (
                  <TicketCreationError
                    error={error}
                    provider="linear"
                    contextId={contextId}
                    integrationId={integration.id}
                  />
                ) : (
                  <Alert severity="error">
                    {error.message || 'Failed to create Linear ticket.'}
                  </Alert>
                ))}

              <FormField title="Team">
                <DropDown
                  size="large"
                  label="Team"
                  options={[{ value: integration.id, label: teamName }]}
                  value={integration.id}
                  disabled
                />
              </FormField>

              <FormField
                title="Title"
                errorMessage={formState.errors.title?.message as string}
              >
                <TextField
                  {...register('title', {
                    required: requiredLabel,
                  })}
                  size="large"
                />
              </FormField>

              <FormField
                title="Description"
                errorMessage={formState.errors.description?.message as string}
              >
                <TextField {...register('description')} multiline rows={20} />
              </FormField>
            </FlexColumn>
          )}
        </form>
      }
    />
  );
};

export default CreateLinearTicketDialog;
